/////////////////////////////////////////////////////////////////////
// Product variants (size, volume, scent, shade, …).                //
//                                                                  //
// A few of the hosted listing fields describe *which version of a  //
// product* is being bought rather than a fact about it: the sizes a //
// garment comes in, the volumes a bottle is sold in, the scents to  //
// choose from. Those don't belong in the product page's list of     //
// specifications — they belong in the order panel as dropdowns, and //
// the shopper's choice has to travel with the order so the brand    //
// knows which one to ship.                                         //
//                                                                  //
// The fields are configured by an operator in Sharetribe Console    //
// and delivered as a hosted asset, so their keys can't be imported  //
// from anywhere in this repo. They are recognised by key *or* label //
// the same way ListingPage/listingFieldGroups.js recognises the     //
// ecolabel and delivery fields. Each field's Console config already //
// limits it to the right listing types and categories, so this      //
// module reuses that configuration rather than repeating it.        //
/////////////////////////////////////////////////////////////////////

import { isFieldForCategory, isFieldForListingType, pickCategoryFields } from './fieldHelpers';
import { SCHEMA_TYPE_MULTI_ENUM, SCHEMA_TYPE_SHORT_TEXT, SCHEMA_TYPE_TEXT } from './types';
import { getCustomVariantFields } from './customVariants';

// "Volume" and "volumeMl" both collapse to "volume", which is what makes
// matching on either the key or the label work.
const normalize = value =>
  String(value == null ? '' : value)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

/**
 * Fields whose value the shopper chooses from. Add to this list when an
 * operator adds another variant field in Console; nothing else needs changing.
 */
export const VARIANT_FIELD_PATTERNS = [
  'size',
  'volume',
  'scent',
  'fragrance',
  'shade',
  'colour',
  'color',
  'flavour',
  'flavor',
];

// A "Size guide" is a spec, not a variant: it happens to contain "size" but
// nothing in it is orderable. Anything matching these loses.
const NOT_A_VARIANT_PATTERNS = ['guide', 'chart', 'howtomeasure', 'instructions', 'description'];

// Sizes are a multi-select in Console; volume/scent/shade are a single line of
// text, e.g. "50ml, 100ml". Other schema types (boolean, long, enum) can't
// express a set of choices, so they are never variants.
const VARIANT_SCHEMA_TYPES = [SCHEMA_TYPE_MULTI_ENUM, SCHEMA_TYPE_TEXT, SCHEMA_TYPE_SHORT_TEXT];

// Vendors type their options as a comma-separated line.
const TEXT_OPTION_SEPARATOR = ',';

/**
 * Is this hosted listing field one the shopper picks a value from?
 *
 * @param {Object} fieldConfig a single entry of config.listing.listingFields
 * @returns {boolean} true when the field defines product variants
 */
export const isProductVariantField = fieldConfig => {
  if (!fieldConfig?.key || !VARIANT_SCHEMA_TYPES.includes(fieldConfig.schemaType)) {
    return false;
  }
  const key = normalize(fieldConfig.key);
  const label = normalize(fieldConfig.showConfig?.label || fieldConfig.saveConfig?.label);
  const matches = pattern => key.includes(pattern) || label.includes(pattern);

  return VARIANT_FIELD_PATTERNS.some(matches) && !NOT_A_VARIANT_PATTERNS.some(matches);
};

/**
 * Split a vendor's comma-separated line ("50ml, 100ml") into options, keeping
 * their order and dropping blanks and repeats.
 *
 * @param {string} text the value the vendor typed
 * @returns {Array<string>} trimmed option values
 */
export const parseTextVariantOptions = text => {
  if (typeof text !== 'string') {
    return [];
  }
  const values = text
    .split(TEXT_OPTION_SEPARATOR)
    .map(part => part.trim())
    .filter(Boolean);
  return [...new Set(values)];
};

/**
 * The options a shopper can choose from for one variant field: the enum values
 * the vendor ticked, or the values they typed.
 *
 * @param {Object} fieldConfig a single entry of config.listing.listingFields
 * @param {*} value the value the vendor saved for that field
 * @returns {Array<Object>} [{ value, label }] in the vendor's own order
 */
export const getVariantOptions = (fieldConfig, value) => {
  if (fieldConfig?.schemaType === SCHEMA_TYPE_MULTI_ENUM) {
    const selected = Array.isArray(value) ? value : [];
    const enumOptions = fieldConfig.enumOptions || [];
    // Console's option order is the meaningful one for sizes (XXS → XXL), so the
    // selected values are read in that order rather than the order they happen
    // to be stored in.
    const orderedSelection = enumOptions.length
      ? enumOptions.filter(o => selected.some(v => `${v}` === `${o.option}`))
      : selected.map(v => ({ option: v }));

    return orderedSelection.map(o => ({ value: `${o.option}`, label: o.label || `${o.option}` }));
  }
  return parseTextVariantOptions(value).map(v => ({ value: v, label: v }));
};

/**
 * Work out which variant dropdowns a listing should offer.
 *
 * A field becomes a dropdown only when it applies to the listing's type and the
 * categories the vendor chose (both straight from the Console config) *and* the
 * vendor actually filled it in.
 *
 * The variants a vendor named themselves (util/customVariants.js) follow the
 * Console ones, in the order the vendor added them.
 *
 * @param {Object} params
 * @param {Object} params.publicData listing.attributes.publicData
 * @param {Object} [params.metadata] listing.attributes.metadata
 * @param {Array} params.listingFieldConfigs config.listing.listingFields
 * @param {Object} params.categoryConfiguration config.categoryConfiguration
 * @returns {Array<Object>} [{ key, label, options: [{ value, label }], isCustom? }]
 */
export const getProductVariantFields = params => {
  const { publicData = {}, metadata = {}, listingFieldConfigs = [], categoryConfiguration = {} } =
    params || {};

  const { key: categoryPrefix, categories: listingCategoriesConfig } = categoryConfiguration;
  const currentCategories = Object.values(
    pickCategoryFields(publicData, categoryPrefix, 1, listingCategoriesConfig)
  );

  const consoleFields = listingFieldConfigs.reduce((acc, fieldConfig) => {
    if (!isProductVariantField(fieldConfig)) {
      return acc;
    }
    if (
      !isFieldForListingType(publicData.listingType, fieldConfig) ||
      !isFieldForCategory(currentCategories, fieldConfig)
    ) {
      return acc;
    }
    const { key, scope = 'public', showConfig, saveConfig } = fieldConfig;
    const value = scope === 'metadata' ? metadata?.[key] : publicData?.[key];
    const options = getVariantOptions(fieldConfig, value);

    return options.length === 0
      ? acc
      : acc.concat({ key, label: showConfig?.label || saveConfig?.label || key, options });
  }, []);

  return consoleFields.concat(getCustomVariantFields(publicData));
};

/**
 * Preselect the fields that only offer one option: there is nothing to decide,
 * and the order can't be placed without a value.
 *
 * @param {Array} variantFields as returned by getProductVariantFields
 * @returns {Object} { [fieldKey]: optionValue }
 */
export const getDefaultVariantSelection = (variantFields = []) =>
  variantFields.reduce(
    (acc, field) =>
      field.options.length === 1 ? { ...acc, [field.key]: field.options[0].value } : acc,
    {}
  );

/**
 * Turn a form's raw selection into the entries that are stored on the order,
 * dropping anything that isn't one of the offered options.
 *
 * The label travels with the value because the hosted config that maps "m" to
 * "Medium" can be edited (or the field removed) long after the order was placed,
 * and the brand still needs to know what they sold.
 *
 * @param {Array} variantFields as returned by getProductVariantFields
 * @param {Object} selection { [fieldKey]: optionValue }
 * @returns {Array<Object>} [{ key, label, value, valueLabel }] in field order
 */
export const getVariantSelectionEntries = (variantFields = [], selection = {}) =>
  variantFields.reduce((acc, field) => {
    const value = selection?.[field.key];
    const option = field.options.find(o => o.value === value);
    return option
      ? acc.concat({
          key: field.key,
          label: field.label,
          value: option.value,
          valueLabel: option.label,
        })
      : acc;
  }, []);

/**
 * Has the shopper chosen every variant the listing asks for?
 *
 * @param {Array} variantFields as returned by getProductVariantFields
 * @param {Object} selection { [fieldKey]: optionValue }
 * @returns {boolean} true when nothing is left to choose
 */
export const hasCompleteVariantSelection = (variantFields = [], selection = {}) =>
  getVariantSelectionEntries(variantFields, selection).length === variantFields.length;

/**
 * The variant part of a transaction's protected data. Empty for listings that
 * don't have variants, so it can always be spread into protectedData.
 *
 * @param {Array} entries as returned by getVariantSelectionEntries
 * @returns {Object} { productVariants } or {}
 */
export const getVariantProtectedDataMaybe = (entries = []) =>
  entries.length > 0 ? { productVariants: entries } : {};

/**
 * A one-line summary of a selection, e.g. "Size: Medium · Volume: 100ml".
 *
 * @param {Array} entries as returned by getVariantSelectionEntries
 * @returns {string} summary, or an empty string when there is no selection
 */
export const formatVariantSummary = (entries = []) =>
  (Array.isArray(entries) ? entries : [])
    .filter(entry => entry?.valueLabel || entry?.value)
    .map(entry =>
      entry.label
        ? `${entry.label}: ${entry.valueLabel || entry.value}`
        : entry.valueLabel || entry.value
    )
    .join(' · ');

/**
 * Name one combination of variants ("size:m|colour:red").
 *
 * Sorting by key means the name doesn't depend on the order the selection
 * happens to be in, so the same combination always produces the same string —
 * which is what lets it double as a basket line id and as the key stock counts
 * are stored under. See util/variantStock.js.
 *
 * @param {Array} entries as returned by getVariantSelectionEntries
 * @returns {string} the combination key ('' when there are no variants)
 */
export const getVariantCombinationKey = (entries = []) =>
  (Array.isArray(entries) ? entries : [])
    .filter(entry => entry?.key && entry?.value != null)
    .map(entry => `${entry.key}:${entry.value}`)
    .sort()
    .join('|');

/**
 * Identify a basket line. Two variants of the same product are two lines, so a
 * listing id alone isn't enough to tell them apart.
 *
 * @param {string} listingId the listing's uuid
 * @param {Array} entries as returned by getVariantSelectionEntries
 * @returns {string} the line id (the listing id itself when there are no variants)
 */
export const getCartLineId = (listingId, entries = []) => {
  const combinationKey = getVariantCombinationKey(entries);
  return combinationKey === '' ? `${listingId}` : `${listingId}::${combinationKey}`;
};
